import { useEffect, useRef, useCallback, useState } from 'react';
import {
  Typography, Space, Tag, Button, Tooltip, Drawer, Divider,
  Progress, Statistic, Modal, Form, Input, Select, message,
} from 'antd';
import {
  MenuFoldOutlined, MenuUnfoldOutlined, InfoCircleOutlined,
  ClearOutlined, RobotOutlined, ThunderboltOutlined, DownloadOutlined,
  CrownOutlined, GiftOutlined, MessageOutlined, ArrowRightOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useChatStore } from '@/stores/chat';
import { aiAPI } from '@/services/api';
import ModelSelector, { cleanModelDisplay } from '@/components/ModelSelector';
import ChatSidebar from './AiChat/ChatSidebar';
import ChatMessageBubble from './AiChat/ChatMessage';
import ChatInput from './AiChat/ChatInput';
import PromptOptimizer from './AiChat/PromptOptimizer';
import QuotaBar from './AiChat/QuotaBar';

const { Text, Title } = Typography;

export default function AiChat() {
  const navigate = useNavigate();
  const {
    sessions, activeSessionId, mode, model, loading, quota, quotaLoading,
    createSession, addMessage, updateMessage, clearMessages,
    setMode, setModel, setLoading, setQuota, setQuotaLoading,
    attachedSkills, attachedWorkflow,
    rightPanelOpen, toggleRightPanel,
  } = useChatStore();

  const handleExportChat = () => {
    const text = messages.map(function(m) { return (m.role === 'user' ? '[用户] ' : '[AI] ') + (typeof m.content === 'string' ? m.content : JSON.stringify(m.content)); }).join('\n\n---\n\n');
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'ai-chat-export.txt'; a.click();
    URL.revokeObjectURL(url);
    message.success('对话已导出');
  };

  /**
   * 客户端规则兜底：提示词优化接口不可用时，本地结构化改写，
   * 保证「用户输入需求 → 主动优化提示词」永远生效。
   */
  const localPromptFallback = useCallback((prompt: string): string => {
    return `# 角色
（请根据需求设定专业角色，例如资深工程师 / 行业顾问 / 内容策划）

# 任务
${prompt}

# 约束
先准确理解用户真实意图；输出前自查是否满足需求；不编造事实，不确定处明确说明。

# 输出格式
按结构化方式组织（分点 / 列表 / 代码块 / 表格），重点突出，可直接使用。`;
  }, []);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [optimizerOpen, setOptimizerOpen] = useState(false);
  const [optimizerPrompt, setOptimizerPrompt] = useState('');
  const [createAgentOpen, setCreateAgentOpen] = useState(false);
  const [agentForm] = Form.useForm();

  // 创建智能体（复用会话机制 + 系统提示词）
  const handleCreateAgent = useCallback(async () => {
    try {
      const values = await agentForm.validateFields();
      createSession({
        title: values.title,
        description: values.description,
        model: values.model || model,
        mode: values.mode || 'qa',
        systemPrompt: values.systemPrompt,
      });
      setCreateAgentOpen(false);
      agentForm.resetFields();
      message.success('智能体已创建，开始对话吧');
    } catch {
      /* 表单校验失败，等待用户修正 */
    }
  }, [createSession, model, agentForm]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const activeSession = sessions.find((s) => s.id === activeSessionId);
  const messages = activeSession?.messages || [];

  // 自动滚动到底部（用户手动上滚时暂停自动跟随）
  const userScrolledUpRef = useRef(false);
  const prevMsgCountRef = useRef(0);
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const currentCount = messages.length;
    if (currentCount > prevMsgCountRef.current || loading) {
      if (!userScrolledUpRef.current) {
        container.scrollTop = container.scrollHeight;
      }
    }
    prevMsgCountRef.current = currentCount;
  }, [messages, loading]);
  // 监听用户手动滚动
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const t = container;
      userScrolledUpRef.current = (t.scrollHeight - t.scrollTop - t.clientHeight) > 80;
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    if (!activeSessionId && sessions.length === 0) {
      createSession();
    }
    userScrolledUpRef.current = false;
    prevMsgCountRef.current = 0;
  }, [activeSessionId, sessions.length, createSession]);

  // 自动优化提示词开关（默认开启：用户输入需求后，先调用「提示词优化大师」主动优化再发送）
  const [autoOptimize, setAutoOptimize] = useState(true);

  // 推理档位（低/中/高）→ temperature / maxTokens，Codex 桌面版同款体验
  const [effort, setEffort] = useState<'low' | 'medium' | 'high'>('medium');
  const effortParams = (e: string) => {
    if (e === 'low') return { temperature: 0.3, maxTokens: 900 };
    if (e === 'high') return { temperature: 0.85, maxTokens: 5000 };
    return { temperature: 0.6, maxTokens: 2400 };
  };

  // ── 实时配额：进入页面拉取一次；每次对话用响应快照增量更新 ──
  const refreshQuota = useCallback(async () => {
    try {
      setQuotaLoading(true);
      const res: any = await aiAPI.getChatQuota();
      setQuota(res?.data ?? null);
    } catch {
      /* 配额接口失败不阻断聊天 */
    } finally {
      setQuotaLoading(false);
    }
  }, [setQuota, setQuotaLoading]);

  useEffect(() => {
    refreshQuota();
  }, [refreshQuota]);

  // 发送消息
  const handleSend = useCallback(
    async (text: string) => {
      if (!text.trim() || loading) return;

      // 自动优化：发送前调用「提示词优化大师」主动优化（后端云函数免费通道 → 技能 → 规则兜底；客户端再兜一层）
      let finalText = text.trim();
      let optimizedFlag = false;
      if (autoOptimize) {
        try {
          const optRes: any = await aiAPI.promptOptimize({ prompt: finalText, direction: 'structured' });
          const optimized = optRes?.data?.optimized || optRes?.optimized;
          if (optimized && optimized.trim()) {
            finalText = optimized.trim();
            optimizedFlag = true;
          } else {
            finalText = localPromptFallback(finalText);
            optimizedFlag = true;
          }
        } catch {
          finalText = localPromptFallback(finalText);
          optimizedFlag = true;
        }
      }

      // 确保有活跃会话
      let sid = activeSessionId;
      if (!sid) {
        sid = createSession();
      }

      // 添加用户消息（气泡展示原文 + 「已优化」标记；发送给 AI 的是优化后的提示词）
      addMessage({ role: 'user', content: text.trim(), model, mode, optimized: optimizedFlag });

      // 重置滚动标记，让新消息自动滚动到底部
      userScrolledUpRef.current = false;

      // 添加空的 AI 消息占位（Codex 风格流式展示）
      const aiMsgId = addMessage({ role: 'assistant', content: '', model, mode, streaming: true });
      setLoading(true);

      try {
        const history = messages.map((m) => ({ role: m.role, content: m.content }));

        // 组装系统提示词：智能体（会话 systemPrompt）+ 附加技能 + 关联工作流
        const sysParts: string[] = [];
        if (activeSession?.systemPrompt) sysParts.push(activeSession.systemPrompt);
        if (attachedWorkflow) {
          sysParts.push(`【关联智能工作流：${attachedWorkflow.name}】${attachedWorkflow.description || '请结合该工作流的思路与步骤回答并执行。'}`);
        }
        for (const sk of attachedSkills) {
          const rules = sk.criticalRules?.length ? ` 规则：${sk.criticalRules.join('；')}` : '';
          sysParts.push(`【技能：${sk.name}】${sk.coreMission || ''}${rules}`);
        }
        const systemPrompt = sysParts.length
          ? `你是 NexMind by AIbak（aibak.site）平台的 AI 助手。请用中文清晰简洁地回答问题。\n\n${sysParts.join('\n\n')}`
          : (activeSession?.systemPrompt || undefined);

        const res: any = await aiAPI.chat({
          message: finalText,
          history: history.slice(-10),
          sessionId: sid,
          model,
          config: {
            ...(systemPrompt ? { systemPrompt } : {}),
            ...effortParams(effort),
          },
        });

        const reply = res?.text || res?.message || res?.reply || res?.data?.message || res?.data?.text || '（AI 无响应，请检查 API 配置）';
        const usageRaw = res?.usage;
        updateMessage(aiMsgId, {
          content: reply,
          streaming: true,
          usage: usageRaw ? {
            promptTokens: Number(usageRaw.prompt_tokens) || 0,
            completionTokens: Number(usageRaw.completion_tokens) || 0,
            totalTokens: Number(usageRaw.total_tokens) || 0,
          } : undefined,
          creditsCharged: Number(res?.creditsCharged) || 0,
        });

        // 实时刷新配额：今日对话次数 / 积分 / 会员
        if (res?.quota) setQuota(res.quota);
      } catch (err: any) {
        const errMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || '请求失败';
        updateMessage(aiMsgId, {
          content: `⚠️ 请求失败：${errMsg}`,
          streaming: false,
        });
        // 402 等额度类错误后刷新一次配额
        refreshQuota();
      } finally {
        setLoading(false);
      }
    },
    [activeSessionId, messages, model, mode, loading, addMessage, createSession, setLoading, setQuota, refreshQuota, autoOptimize, localPromptFallback, attachedSkills, attachedWorkflow]
  );

  // 流式打字完成 → 清除 streaming 标记
  const handleStreamDone = useCallback((id: string) => {
    updateMessage(id, { streaming: false });
  }, [updateMessage]);

  // 打开提示词优化
  const handleOpenOptimizer = useCallback((text: string) => {
    if (!text) return;
    setOptimizerPrompt(text);
    setOptimizerOpen(true);
  }, []);

  // 移动端侧边栏
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;

  // 客户端只能准确统计字符数；真实 Token/计费数据应以后端返回为准。
  const totalChars = messages.reduce((sum, m) => sum + m.content.length, 0);
  const userChars = messages.filter((m) => m.role === 'user').reduce((sum, m) => sum + m.content.length, 0);
  const turnCount = messages.filter((m) => m.role === 'user').length;
  const totalTokens = messages.reduce((sum, m) => sum + (m.usage?.totalTokens || 0), 0);
  const totalCreditsUsed = messages.reduce((sum, m) => sum + (m.creditsCharged || 0), 0);

  const quotaState = quota?.quota;
  const exhausted = !!quotaState && quotaState.limit !== -1 && quotaState.remaining === 0;
  const member = !!quota && quota.plan !== 'free' && !quota.expired;

  return (
    <div className="chat-container" ref={containerRef}>
      {/* ===== 左侧边栏（Codex 风格会话线程） ===== */}
      {!isMobile && sidebarOpen && <ChatSidebar onOpenCreateAgent={() => setCreateAgentOpen(true)} />}
      {isMobile && (
        <Drawer
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          width={280}
          placement="left"
          styles={{ body: { padding: 0, background: 'var(--bg-sidebar)' } }}
        >
          <ChatSidebar onOpenCreateAgent={() => setCreateAgentOpen(true)} />
        </Drawer>
      )}

      {/* ===== 中间主聊天区 ===== */}
      <div className="chat-main">
        {/* 顶部工具栏 */}
        <div className="chat-topbar">
          <Space size={8}>
            <Tooltip title={sidebarOpen ? '收起侧边栏' : '展开侧边栏'}>
              <Button
                type="text"
                icon={sidebarOpen ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="topbar-btn"
              />
            </Tooltip>

            <div className="mode-indicator">
              <Tag
                color={mode === 'qa' ? '#6366f1' : mode === 'plan' ? '#f59e0b' : '#10b981'}
                style={{ borderRadius: 12, padding: '2px 10px', border: 'none', margin: 0 }}
              >
                {mode === 'qa' ? '💬 问答模式' : mode === 'plan' ? '📋 计划模式' : '⚡ 直接执行'}
              </Tag>
            </div>
          </Space>

          <Space size={8}>
            <Text type="secondary" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>🤖 模型</Text>
            <ModelSelector value={model} onChange={setModel} style={{ width: 200 }} size="small" placeholder="选择模型" mode="chat" />

            <Tooltip title="导出对话">
              <Button type="text" icon={<DownloadOutlined />} onClick={handleExportChat} className="topbar-btn" />
            </Tooltip>
            <Tooltip title="清空对话">
              <Button type="text" icon={<ClearOutlined />} onClick={clearMessages} disabled={messages.length === 0} className="topbar-btn" />
            </Tooltip>
            <Tooltip title="会话详情">
              <Button
                type="text"
                icon={<InfoCircleOutlined />}
                onClick={toggleRightPanel}
                className="topbar-btn"
                style={{ color: rightPanelOpen ? '#6366f1' : undefined }}
              />
            </Tooltip>
          </Space>
        </div>

        {/* 免费额度 / 积分 / 会员 实时状态条 */}
        <QuotaBar quota={quota} loading={quotaLoading} />

        {/* 消息列表 */}
        <div className="chat-messages" ref={messagesContainerRef}>
          {messages.length === 0 ? (
            <div className="chat-empty">
              <div className="empty-icon">
                <RobotOutlined style={{ fontSize: 46, color: '#a5b4fc' }} />
              </div>
              <Title level={4} className="empty-title">开始对话</Title>
              <Text className="empty-sub">选择模型和模式，输入问题开始体验 · 今日对话实时计数</Text>
              <Button
                type="primary"
                icon={<RobotOutlined />}
                onClick={() => setCreateAgentOpen(true)}
                className="empty-agent-btn"
              >
                创建智能体（Agent）
              </Button>
              <div className="quick-tags">
                {[
                  { t: '代码解释', m: '请解释以下代码的逻辑：' },
                  { t: '技术问答', m: '请详细解释什么是 RAG？' },
                  { t: '文档总结', m: '请帮我把以下内容总结成要点：' },
                  { t: '方案策划', m: '请帮我在以下方面做一个方案：' },
                  { t: '翻译对比', m: '请把以下内容翻译成英文：' },
                  { t: '知识检索', m: '请检索知识库中关于：' },
                ].map((qt) => (
                  <Tag
                    key={qt.t}
                    className="quick-tag"
                    onClick={() => handleSend(qt.m)}
                  >
                    {qt.t}
                  </Tag>
                ))}
              </div>
            </div>
          ) : (
            <div className="message-list">
              {messages.map((msg, i) => (
                <ChatMessageBubble key={msg.id || i} msg={msg} onStreamDone={handleStreamDone} />
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* 输入区域 + 额度用尽引导 */}
        <div className="chat-input-wrap">
          {exhausted && (
            <div className="quota-exhausted-banner">
              <Space size={6}>
                <CrownOutlined />
                <span>今日免费对话次数已用完</span>
              </Space>
              <Space size={4}>
                {!quota?.authenticated && (
                  <Button size="small" type="link" icon={<ArrowRightOutlined />} onClick={() => navigate('/login?returnTo=' + encodeURIComponent('/ai-chat'))}>
                    登录领取免费额度
                  </Button>
                )}
                <Button size="small" type="primary" ghost onClick={() => navigate('/pricing')}>
                  升级套餐
                </Button>
                <Button size="small" type="link" onClick={() => navigate('/points-center')}>
                  积分中心
                </Button>
              </Space>
            </div>
          )}
          <ChatInput
            onSend={(text) => handleSend(text)}
            onPromptOptimize={handleOpenOptimizer}
            autoOptimize={autoOptimize}
            onToggleAutoOptimize={setAutoOptimize}
            disabled={loading}
            exhausted={exhausted}
            onOpenCreateAgent={() => setCreateAgentOpen(true)}
          />
        </div>
      </div>

      {/* ===== 右侧面板：会话详情（含对话次数 / 积分 / 会员） ===== */}
      {rightPanelOpen && (
        <div className="chat-right-panel">
          <Title level={5} style={{ margin: 0, color: 'var(--text-primary)' }}>会话详情</Title>
          <Divider style={{ margin: '12px 0' }} />

          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            <div>
              <Text className="panel-label">当前模型</Text>
              <div className="panel-value">{cleanModelDisplay(model)}</div>
            </div>

            <div>
              <Text className="panel-label">对话模式</Text>
              <div className="panel-value">
                {mode === 'qa' ? '问答模式' : mode === 'plan' ? '计划模式' : '直接执行'}
              </div>
            </div>

            <Divider style={{ margin: '4px 0' }} />

            <div className="panel-grid">
              <Statistic title="消息数" value={messages.length} valueStyle={{ fontSize: 18 }} />
              <Statistic title="对话轮次" value={turnCount} valueStyle={{ fontSize: 18 }} />
              <Statistic title="Token 用量" value={totalTokens} valueStyle={{ fontSize: 18 }} />
              <Statistic title="积分消耗" value={totalCreditsUsed} valueStyle={{ fontSize: 18 }} />
            </div>

            {/* 会员 / 额度 */}
            <div className="panel-block">
              <Text className="panel-label">会员状态</Text>
              <div className="panel-value">
                {member ? <CrownOutlined style={{ color: '#f59e0b' }} /> : null}{' '}
                {quota
                  ? (member ? '会员' : quota.plan === 'free' ? '免费版' : (quota.plan + (quota.expired ? '（已过期）' : '')))
                  : '--'}
                {quota?.membershipExpiresAt && member ? ` · ${new Date(quota.membershipExpiresAt).toLocaleDateString('zh-CN')}到期` : ''}
              </div>
            </div>

            <div className="panel-block">
              <Text className="panel-label">今日对话额度</Text>
              {quotaState && quotaState.limit !== -1 ? (
                <div className="panel-value">
                  {quotaState.used} / {quotaState.limit} 次 · 剩余 {Math.max(0, quotaState.remaining)} 次
                </div>
              ) : (
                <div className="panel-value">会员无限次</div>
              )}
              {quotaState && quotaState.limit !== -1 && (
                <Progress
                  percent={quotaState.limit > 0 ? Math.min(100, Math.round((quotaState.used / quotaState.limit) * 100)) : 0}
                  strokeColor={quotaState.remaining === 0 ? '#f5222d' : '#6366f1'}
                  trailColor="rgba(148,163,184,0.2)"
                  size="small"
                />
              )}
            </div>

            <div className="panel-block">
              <Text className="panel-label">可用积分</Text>
              <div className="panel-value"><GiftOutlined /> {quota?.credits ?? 0}</div>
            </div>

            <div className="panel-block">
              <Text className="panel-label">字符分布</Text>
              <Progress
                percent={totalChars > 0 ? Math.round((userChars / totalChars) * 100) : 0}
                strokeColor="#6366f1"
                trailColor="rgba(148,163,184,0.2)"
                size="small"
                format={() => `用户 ${userChars}`}
              />
            </div>
          </Space>
        </div>
      )}

      {/* 提示词优化弹窗 */}
      <PromptOptimizer
        open={optimizerOpen}
        onClose={() => setOptimizerOpen(false)}
        originalPrompt={optimizerPrompt}
        currentModel={model}
        onApply={(optimized) => {
          setOptimizerOpen(false);
          handleSend(optimized);
        }}
      />

      {/* 创建智能体（Agent）弹窗 */}
      <Modal
        title={
          <Space>
            <RobotOutlined />
            创建智能体（Agent）
          </Space>
        }
        open={createAgentOpen}
        onCancel={() => { setCreateAgentOpen(false); agentForm.resetFields(); }}
        onOk={handleCreateAgent}
        okText="创建并对话"
        width={560}
        destroyOnClose
      >
        <Form form={agentForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="title" label="名称" rules={[{ required: true, message: '请输入智能体名称' }]}>
            <Input placeholder="如：Python 编程导师" maxLength={40} />
          </Form.Item>
          <Form.Item name="description" label="描述（可选）">
            <Input placeholder="如：擅长讲解 Python 并给出示例" maxLength={80} />
          </Form.Item>
          <Form.Item name="model" label="模型" initialValue={model}>
            <ModelSelector style={{ width: "100%" }} mode="chat" />
          </Form.Item>
          <Form.Item name="mode" label="对话模式" initialValue="qa">
            <Select options={[
              { label: '问答模式', value: 'qa' },
              { label: '计划模式', value: 'plan' },
              { label: '直接执行', value: 'execute' },
            ]} />
          </Form.Item>
          <Form.Item
            name="systemPrompt"
            label="系统提示词"
            rules={[{ required: true, message: '请设定智能体的角色与行为规范' }]}
            extra="定义该智能体的身份、擅长领域与回答风格，对话时自动生效。"
          >
            <Input.TextArea
              rows={4}
              placeholder="例如：你是一位资深的 Python 编程导师，讲解清晰、循序渐进，多用示例说明。"
            />
          </Form.Item>
        </Form>
      </Modal>

      <style>{`
        .chat-container {
          display: flex; height: calc(100vh - 88px); overflow: hidden;
          background: var(--bg-base); border-radius: 0; margin: -24px;
          color: var(--text-primary);
        }

        .chat-main {
          flex: 1; display: flex; flex-direction: column; min-width: 0;
          background: var(--bg-base);
        }

        .chat-topbar {
          display: flex; justify-content: space-between; align-items: center;
          padding: 8px 14px; border-bottom: 1px solid var(--border);
          background: var(--bg-container);
        }
        .topbar-btn { color: var(--text-secondary); }
        .mode-indicator { display: flex; align-items: center; }

        /* ── 免费额度状态条 ── */
        .chat-quota-bar {
          display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 6px 12px;
          padding: 5px 16px; border-bottom: 1px solid var(--border);
          background: var(--bg-container); font-size: 12px; color: var(--text-secondary);
        }
        .quota-items { row-gap: 2px !important; }
        .quota-item { display: inline-flex; align-items: center; gap: 5px; }
        .quota-item svg { color: var(--text-tertiary); }
        .quota-item b { color: var(--text-primary); font-weight: 600; }
        .quota-muted { color: var(--text-tertiary); }
        .quota-item.quota-warn b { color: #faad14; }
        .quota-item.quota-member b { color: #f59e0b; }
        .quota-progress { width: 64px; margin: 0 2px; }
        .chat-quota-bar.quota-low { background: rgba(250, 173, 20, 0.06); }
        .chat-quota-bar.quota-exhausted { background: rgba(245, 34, 45, 0.05); }
        .quota-cta { display: inline-flex; align-items: center; }

        .chat-messages {
          flex: 1; overflow-y: auto; padding: 16px 20px;
          scroll-behavior: smooth;
        }
        .message-list {
          max-width: 820px; margin: 0 auto;
          display: flex; flex-direction: column; gap: 2px;
        }

        .chat-empty {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          height: 100%; text-align: center; padding: 40px 20px;
        }
        .empty-icon {
          width: 78px; height: 78px; border-radius: 22px;
          background: linear-gradient(135deg, rgba(99,102,241,0.12), rgba(139,92,246,0.12));
          border: 1px solid rgba(99,102,241,0.25);
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 8px 30px rgba(99,102,241,0.15);
        }
        .empty-title { color: var(--text-primary); margin: 14px 0 4px !important; }
        .empty-sub { color: var(--text-tertiary); font-size: 13px; }
        .empty-agent-btn {
          margin-top: 16px; border-radius: 10px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6); border: none;
        }
        .quick-tags {
          display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin-top: 16px;
          max-width: 460px;
        }
        .quick-tag {
          cursor: pointer; border-radius: 14px; padding: 3px 14px;
          border: 1px solid var(--border); background: var(--bg-container);
          color: var(--text-secondary); transition: all 0.15s;
        }
        .quick-tag:hover { border-color: rgba(99,102,241,0.5); color: var(--text-primary); }

        .loading-indicator {
          display: flex; gap: 6px; padding: 8px 0 8px 44px; align-items: center;
        }
        .loading-dot {
          width: 6px; height: 6px; background: #a5b4fc; border-radius: 50%;
          animation: dotPulse 1.2s ease-in-out infinite;
        }
        .loading-dot:nth-child(2) { animation-delay: 0.2s; }
        .loading-dot:nth-child(3) { animation-delay: 0.4s; }
        @keyframes dotPulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }

        /* ── 输入区 + 额度用尽引导 ── */
        .chat-input-wrap {
          background: var(--bg-container); border-top: 1px solid var(--border);
        }
        .quota-exhausted-banner {
          display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 6px;
          padding: 6px 16px; font-size: 12.5px; color: #f5222d;
          background: linear-gradient(90deg, rgba(245,34,45,0.08), rgba(99,102,241,0.08));
          border-bottom: 1px solid rgba(245,34,45,0.12);
        }

        .chat-right-panel {
          width: 300px; padding: 16px; border-left: 1px solid var(--border);
          background: var(--bg-container); overflow-y: auto; color: var(--text-primary);
        }
        .panel-label { font-size: 12px; color: var(--text-tertiary); display: block; margin-bottom: 2px; }
        .panel-value { font-size: 15px; font-weight: 500; color: var(--text-primary); margin-top: 2px; }
        .panel-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .panel-grid .ant-statistic-title { font-size: 12px; color: var(--text-tertiary); }
        .panel-grid .ant-statistic-content { color: var(--text-primary); }
        .panel-block { background: var(--bg-elevated); border: 1px solid var(--border); border-radius: 10px; padding: 10px 12px; }

        /* 滚动条美化 */
        .chat-messages::-webkit-scrollbar,
        .chat-right-panel::-webkit-scrollbar,
        .session-scroll::-webkit-scrollbar { width: 5px; }
        .chat-messages::-webkit-scrollbar-thumb,
        .chat-right-panel::-webkit-scrollbar-thumb,
        .session-scroll::-webkit-scrollbar-thumb {
          background: var(--border); border-radius: 3px;
        }

        @media (max-width: 1023px) {
          .chat-container { margin: -16px; border-radius: 0; }
          .chat-right-panel { display: none; }
          .chat-messages { padding: 12px; }
        }
        @media (max-width: 768px) {
          .chat-container { height: calc(100vh - 128px); }
          .chat-topbar { padding: 8px; }
          .mode-indicator { display: none; }
          .chat-quota-bar { padding: 4px 12px; font-size: 11px; }
        }
      `}</style>
    </div>
  );
}
